package com.patronat.top.features.venues.presentation

import androidx.lifecycle.viewModelScope
import com.patronat.top.core.base.BaseViewModel
import com.patronat.top.core.presentation.AlertController
import com.patronat.top.core.presentation.NotificationType
import com.patronat.top.core.utils.UiText
import com.patronat.top.dto.UNASSIGNED_VENUE_OWNER_ID
import com.patronat.top.dto.UserRole
import com.patronat.top.dto.VenueAiImportMode
import com.patronat.top.dto.VenueAiImportRequest
import com.patronat.top.dto.VenueFilterRequest
import com.patronat.top.dto.venues.VenueStatus
import com.patronat.top.features.venues.domain.usecase.GetFilteredVenuesUseCase
import com.patronat.top.features.venues.domain.usecase.ImportVenueWithAiUseCase
import com.patronat.top.features.venues.domain.usecase.UpdateVenueStatusUseCase
import com.patronat.top.features.venues.domain.usecase.command.GetFilteredVenuesCommand
import com.patronat.top.features.venues.domain.usecase.command.ImportVenueWithAiCommand
import com.patronat.top.features.venues.domain.usecase.command.UpdateVenueStatusCommand
import com.patronat.top.features.usermanagement.domain.usecase.GetUserByIdCommand
import com.patronat.top.features.usermanagement.domain.usecase.GetUserByIdUseCase
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import top.features.venues.generated.resources.Res
import top.features.venues.generated.resources.venue_error_block_failed
import top.features.venues.generated.resources.venue_error_load_list_failed
import top.features.venues.generated.resources.venue_success_blocked_message
import top.features.venues.generated.resources.venue_success_blocked_title
import top.design_system.generated.resources.Res as DesignRes
import top.design_system.generated.resources.error_alert_title

class VenuesListViewModel(
    private val alertController: AlertController,
    private val getFilteredVenuesUseCase: GetFilteredVenuesUseCase,
    private val importVenueWithAiUseCase: ImportVenueWithAiUseCase,
    private val updateVenueStatusUseCase: UpdateVenueStatusUseCase,
    private val getUserByIdUseCase: GetUserByIdUseCase
) : BaseViewModel<VenuesListState, VenuesListEvent, VenuesListEffect>(
    initialState = VenuesListState()
) {

    private var searchJob: Job? = null
    private var addVenueSearchJob: Job? = null

    override fun onEvent(event: VenuesListEvent) {
        when (event) {
            is VenuesListEvent.Initialize -> {
                updateState {
                    it.copy(
                        userRole = event.userRole,
                        currentUserId = event.currentUserId,
                        selectedStatusFilterIds = emptyList(),
                        searchQuery = "",
                        currentPage = 1,
                        expandedVenueMenuId = null,
                        ownersById = emptyMap(),
                        failedOwnerIds = emptySet(),
                        moderatorsById = emptyMap(),
                        failedModeratorIds = emptySet(),
                        isAddVenueDialogVisible = false,
                        addVenueActionMode = AddVenueActionMode.CREATE_NEW,
                        addVenueSearchQuery = "",
                        addVenueCandidates = emptyList(),
                        selectedAddVenueCandidateId = null,
                        isAddVenueCandidatesLoading = false,
                        addVenueAiWebsiteUrl = "",
                        addVenueAiCreateImmediately = false,
                        isAddVenueAiLoading = false
                    )
                }
                loadData(page = 1)
            }

            VenuesListEvent.OnReload -> loadData(page = 1)
            VenuesListEvent.OnAddVenueClicked -> {
                if (
                    currentState.userRole == UserRole.BUSINESS_OWNER ||
                    currentState.userRole == UserRole.MODERATOR ||
                    currentState.userRole == UserRole.SUPER_ADMIN
                ) {
                    openAddVenueDialog()
                } else {
                    sendEffect(VenuesListEffect.OpenCreateVenue)
                }
            }
            VenuesListEvent.OnDismissAddVenueDialog -> {
                addVenueSearchJob?.cancel()
                updateState {
                    it.copy(
                        isAddVenueDialogVisible = false,
                        addVenueActionMode = AddVenueActionMode.CREATE_NEW,
                        addVenueSearchQuery = "",
                        addVenueCandidates = emptyList(),
                        selectedAddVenueCandidateId = null,
                        isAddVenueCandidatesLoading = false,
                        addVenueAiWebsiteUrl = "",
                        addVenueAiCreateImmediately = false,
                        isAddVenueAiLoading = false
                    )
                }
            }
            is VenuesListEvent.OnAddVenueActionModeChanged -> {
                updateState {
                    it.copy(
                        addVenueActionMode = event.mode,
                        selectedAddVenueCandidateId = null,
                        addVenueCandidates = if (event.mode == AddVenueActionMode.REQUEST_ACCESS) {
                            it.addVenueCandidates
                        } else {
                            emptyList()
                        },
                        addVenueAiWebsiteUrl = if (event.mode == AddVenueActionMode.AI_IMPORT) {
                            it.addVenueAiWebsiteUrl
                        } else {
                            ""
                        },
                        addVenueAiCreateImmediately = if (event.mode == AddVenueActionMode.AI_IMPORT) {
                            it.addVenueAiCreateImmediately
                        } else {
                            false
                        }
                    )
                }
                if (event.mode == AddVenueActionMode.REQUEST_ACCESS) {
                    loadAddVenueCandidates(query = currentState.addVenueSearchQuery)
                }
            }
            is VenuesListEvent.OnAddVenueCandidateQueryChanged -> {
                updateState { it.copy(addVenueSearchQuery = event.query) }
                addVenueSearchJob?.cancel()
                addVenueSearchJob = viewModelScope.launch {
                    delay(350)
                    if (currentState.addVenueActionMode == AddVenueActionMode.REQUEST_ACCESS) {
                        loadAddVenueCandidates(query = event.query)
                    }
                }
            }
            is VenuesListEvent.OnAddVenueCandidateSelected -> {
                updateState { it.copy(selectedAddVenueCandidateId = event.venueId) }
            }
            is VenuesListEvent.OnAddVenueAiWebsiteChanged -> {
                updateState { it.copy(addVenueAiWebsiteUrl = event.url) }
            }
            is VenuesListEvent.OnAddVenueAiCreateImmediatelyChanged -> {
                updateState { it.copy(addVenueAiCreateImmediately = event.createImmediately) }
            }
            VenuesListEvent.OnConfirmAddVenueAction -> confirmAddVenueAction()

            is VenuesListEvent.OnSearchChanged -> {
                updateState { it.copy(searchQuery = event.query) }
                searchJob?.cancel()
                searchJob = viewModelScope.launch {
                    delay(500)
                    loadData(page = 1)
                }
            }

            is VenuesListEvent.OnStatusFilterChanged -> {
                val selected = event.filterIds.takeLast(1)
                updateState { it.copy(selectedStatusFilterIds = selected) }
                loadData(page = 1)
            }

            is VenuesListEvent.OnPageChanged -> loadData(page = event.page)
            is VenuesListEvent.OnPageSizeChanged -> {
                updateState { it.copy(pageSize = event.pageSize, currentPage = 1) }
                loadData(page = 1)
            }

            is VenuesListEvent.OnVenueActionClicked -> {
                updateState { state ->
                    val nextExpanded = if (state.expandedVenueMenuId == event.venueId) {
                        null
                    } else {
                        event.venueId
                    }
                    state.copy(expandedVenueMenuId = nextExpanded)
                }
            }

            VenuesListEvent.OnDismissVenueMenu -> updateState { it.copy(expandedVenueMenuId = null) }
            is VenuesListEvent.OnEditVenueClicked -> {
                updateState { it.copy(expandedVenueMenuId = null) }
                sendEffect(VenuesListEffect.OpenEditVenue(event.venueId))
            }

            is VenuesListEvent.OnBlockVenueClicked -> {
                updateState { it.copy(expandedVenueMenuId = null) }
                blockVenue(event.venueId)
            }
        }
    }

    private fun openAddVenueDialog() {
        updateState {
            it.copy(
                isAddVenueDialogVisible = true,
                addVenueActionMode = AddVenueActionMode.CREATE_NEW,
                addVenueSearchQuery = "",
                addVenueCandidates = emptyList(),
                selectedAddVenueCandidateId = null,
                isAddVenueCandidatesLoading = false,
                addVenueAiWebsiteUrl = "",
                addVenueAiCreateImmediately = false,
                isAddVenueAiLoading = false
            )
        }
    }

    private fun confirmAddVenueAction() {
        when (currentState.addVenueActionMode) {
            AddVenueActionMode.CREATE_NEW -> {
                updateState { it.copy(isAddVenueDialogVisible = false) }
                sendEffect(VenuesListEffect.OpenCreateVenue)
            }
            AddVenueActionMode.REQUEST_ACCESS -> {
                val selectedVenueId = currentState.selectedAddVenueCandidateId ?: return
                updateState { it.copy(isAddVenueDialogVisible = false) }
                sendEffect(VenuesListEffect.OpenEditVenue(selectedVenueId))
            }
            AddVenueActionMode.AI_IMPORT -> importVenueWithAi()
        }
    }

    private fun importVenueWithAi() {
        val websiteUrl = currentState.addVenueAiWebsiteUrl.trim()
        if (websiteUrl.isBlank()) {
            handleError(UiText.DynamicString("Вкажіть посилання на сайт закладу"))
            return
        }

        val mode = if (currentState.addVenueAiCreateImmediately) {
            VenueAiImportMode.CREATE
        } else {
            VenueAiImportMode.PREFILL
        }

        launchNetwork(
            onLoading = { loading -> updateState { it.copy(isAddVenueAiLoading = loading) } },
            request = {
                importVenueWithAiUseCase.execute(
                    ImportVenueWithAiCommand(
                        VenueAiImportRequest(
                            websiteUrl = websiteUrl,
                            mode = mode,
                            uploadPhotos = true
                        )
                    )
                )
            },
            onSuccess = { response ->
                updateState {
                    it.copy(
                        isAddVenueDialogVisible = false,
                        addVenueActionMode = AddVenueActionMode.CREATE_NEW,
                        addVenueAiWebsiteUrl = "",
                        addVenueAiCreateImmediately = false,
                        isAddVenueAiLoading = false
                    )
                }
                val createdVenue = response.createdVenue
                if (createdVenue != null) {
                    val successMessage = if (response.uploadedPhotoMediaIds.isNotEmpty()) {
                        "AI імпортував дані та завантажив фото."
                    } else {
                        "AI імпортував дані. Фото не знайшов, їх можна додати пізніше."
                    }
                    alertController.show(
                        title = UiText.DynamicString("Заклад створено"),
                        message = UiText.DynamicString(successMessage),
                        type = NotificationType.SUCCESS
                    )
                    loadData(page = 1)
                } else {
                    sendEffect(VenuesListEffect.OpenCreateVenueFromAi(response.draft))
                }
            },
            onError = { _, message ->
                val uiMessage = message?.let { UiText.DynamicString(it) }
                    ?: UiText.DynamicString("Не вдалося імпортувати заклад за допомогою AI")
                handleError(uiMessage)
            }
        )
    }

    private fun loadAddVenueCandidates(query: String) {
        val request = VenueFilterRequest(
            page = 1,
            pageSize = 50,
            query = query.ifBlank { null },
            status = VenueStatus.ADDED_BY_MODERATOR,
            ownerId = null
        )

        launchNetwork(
            onLoading = { loading ->
                updateState { it.copy(isAddVenueCandidatesLoading = loading) }
            },
            request = { getFilteredVenuesUseCase.execute(GetFilteredVenuesCommand(request)) },
            onSuccess = { response ->
                updateState { state ->
                    val selectedStillExists = response.items.any { it.id == state.selectedAddVenueCandidateId }
                    state.copy(
                        addVenueCandidates = response.items,
                        selectedAddVenueCandidateId = if (selectedStillExists) state.selectedAddVenueCandidateId else null
                    )
                }
                loadVenueOwners(response.items)
            },
            onError = { _, _ ->
                updateState { it.copy(addVenueCandidates = emptyList(), selectedAddVenueCandidateId = null) }
            }
        )
    }

    private fun loadData(page: Int) {
        val userRole = currentState.userRole
        if (
            (userRole == UserRole.BUSINESS_OWNER || userRole == UserRole.MODERATOR) &&
            currentState.currentUserId.isNullOrBlank()
        ) {
            updateState {
                it.copy(
                    venues = emptyList(),
                    totalCount = 0,
                    currentPage = 1,
                    error = null,
                    isLoading = false
                )
            }
            return
        }
        val ownerId = if (userRole == UserRole.BUSINESS_OWNER) currentState.currentUserId else null
        val moderatorId = if (userRole == UserRole.MODERATOR) currentState.currentUserId else null
        val selectedBackendStatus = currentState.selectedStatusFilterIds.firstOrNull()
            ?.let(VenueStatusFilterPreset::fromId)
            ?.backendStatus

        val request = VenueFilterRequest(
            page = page,
            pageSize = currentState.pageSize,
            query = currentState.searchQuery.ifBlank { null },
            status = selectedBackendStatus,
            ownerId = ownerId,
            moderatorId = moderatorId
        )

        launchNetwork(
            onLoading = { loading -> updateState { it.copy(isLoading = loading) } },
            request = { getFilteredVenuesUseCase.execute(GetFilteredVenuesCommand(request)) },
            onSuccess = { response ->
                updateState {
                    it.copy(
                        venues = response.items,
                        totalCount = response.totalCount,
                        currentPage = response.page,
                        pageSize = response.pageSize,
                        error = null
                    )
                }
                loadVenueModerators(response.items)
                loadVenueOwners(response.items)
            },
            onError = { _, message ->
                updateState { it.copy(error = message) }
                val uiMessage = message?.let { UiText.DynamicString(it) }
                    ?: UiText.StringResource(Res.string.venue_error_load_list_failed)
                handleError(uiMessage)
            }
        )
    }

    private fun loadVenueOwners(venues: List<com.patronat.top.dto.VenueResponse>) {
        val missingOwnerIds = venues
            .map { it.ownerId }
            .filter { it.isNotBlank() && it != UNASSIGNED_VENUE_OWNER_ID }
            .distinct()
            .filterNot { ownerId ->
                currentState.ownersById.containsKey(ownerId) || currentState.failedOwnerIds.contains(ownerId)
            }

        missingOwnerIds.forEach { ownerId ->
            launchNetwork(
                request = { getUserByIdUseCase.execute(GetUserByIdCommand(ownerId)) },
                onSuccess = { owner ->
                    updateState { state ->
                        state.copy(ownersById = state.ownersById + (ownerId to owner))
                    }
                },
                onError = { _, _ ->
                    updateState { state ->
                        state.copy(failedOwnerIds = state.failedOwnerIds + ownerId)
                    }
                }
            )
        }
    }

    private fun loadVenueModerators(venues: List<com.patronat.top.dto.VenueResponse>) {
        val missingModeratorIds = venues
            .mapNotNull { it.moderatorId }
            .filter { it.isNotBlank() }
            .distinct()
            .filterNot { moderatorId ->
                currentState.moderatorsById.containsKey(moderatorId) ||
                    currentState.failedModeratorIds.contains(moderatorId)
            }

        missingModeratorIds.forEach { moderatorId ->
            launchNetwork(
                request = { getUserByIdUseCase.execute(GetUserByIdCommand(moderatorId)) },
                onSuccess = { moderator ->
                    updateState { state ->
                        state.copy(moderatorsById = state.moderatorsById + (moderatorId to moderator))
                    }
                },
                onError = { _, _ ->
                    updateState { state ->
                        state.copy(failedModeratorIds = state.failedModeratorIds + moderatorId)
                    }
                }
            )
        }
    }

    private fun blockVenue(venueId: String) {
        launchNetwork(
            onLoading = { loading -> updateState { it.copy(isLoading = loading) } },
            request = {
                updateVenueStatusUseCase.execute(
                    UpdateVenueStatusCommand(
                        venueId = venueId,
                        status = VenueStatus.REJECTED
                    )
                )
            },
            onSuccess = {
                alertController.show(
                    title = UiText.StringResource(Res.string.venue_success_blocked_title),
                    message = UiText.StringResource(Res.string.venue_success_blocked_message),
                    type = NotificationType.SUCCESS
                )
                loadData(currentState.currentPage)
            },
            onError = { _, message ->
                val uiMessage = message?.let { UiText.DynamicString(it) }
                    ?: UiText.StringResource(Res.string.venue_error_block_failed)
                handleError(uiMessage)
            }
        )
    }

    private fun handleError(message: UiText) {
        alertController.show(
            title = UiText.StringResource(DesignRes.string.error_alert_title),
            message = message,
            type = NotificationType.ERROR
        )
    }
}
